import RAPIER from '@dimforge/rapier3d-compat';
import type { Collider, RigidBody, World } from '@dimforge/rapier3d-compat';

import { WALL } from '../../game/constants';
import { SCORE_ROW, slotsLeftToRight } from '../../game/scoreRow';

/**
 * Build the scoring row's collision geometry.
 *
 * The row is FIXED and lives in WORLD space, so unlike the maze's colliders
 * these are placed at their absolute coordinates and never move. There is no
 * body-local frame to be careful about because nothing ever rotates this body.
 *
 * ---------------------------------------------------------------------------
 * THE ROW IS NOT A SENSOR
 * ---------------------------------------------------------------------------
 * None of these colliders participates in scoring. A pocket is resolved purely
 * from the ball's X as it crosses the row's mouth, by `src/game/scoreRow.ts`.
 * The colliders exist for one reason: to physically catch the ball, so that a
 * round ends in exactly one place and the score can be paid exactly once. A
 * sensor would let the ball pass through the row and out of the world, and the
 * "which pocket" answer would then depend on a trigger volume that the player
 * can see through.
 *
 * Every collider here corresponds to a panel `board.add_scoring_row()` draws,
 * and vice versa:
 *
 *   floor    x1        the shared bottom
 *   dividers x(count+1) the pocket walls, INCLUDING the two end walls
 *   front lip x1       the glass the player watches through
 *   backs    x(count)  one coloured panel per pocket
 *
 * The counts are asserted in `tests/verify-integration.mjs`, because "the row
 * has one more divider than it has pockets" is the kind of off-by-one that
 * silently merges two pockets.
 */
export function buildScoreRowColliders(world: World, body: RigidBody): Collider[] {
  const colliders: Collider[] = [];
  const row = SCORE_ROW;

  const box = (halfX: number, halfY: number, halfZ: number, x: number, y: number, z: number) =>
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(halfX, halfY, halfZ)
        .setTranslation(x, y, z)
        .setFriction(WALL.friction)
        .setRestitution(WALL.restitution),
      body,
    );

  // Floor.
  colliders.push(box(row.totalWidth / 2, row.divider / 2, row.zExtent / 2, 0, row.floorY, row.midZ));

  // Dividers, including the two end walls, so the row is closed at both ends.
  for (let index = 0; index <= row.count; index += 1) {
    const x = -row.span + row.width * index;
    colliders.push(box(row.divider / 2, row.depth / 2, row.zExtent / 2, x, row.midY, row.midZ));
  }

  // Front lip - the transparent panel the player watches the ball through.
  colliders.push(box(row.totalWidth / 2, row.depth / 2, row.divider / 2, 0, row.midY, row.frontZ));

  // One back panel per pocket.
  for (const entry of slotsLeftToRight()) {
    colliders.push(
      box(row.width / 2, row.depth / 2, row.divider / 2, entry.centerX, row.midY, row.backZ),
    );
  }

  return colliders;
}

/** How many colliders `buildScoreRowColliders` produces. Used by the tests. */
export const SCORE_ROW_COLLIDER_COUNT = 2 + SCORE_ROW.count + 1 + SCORE_ROW.count;
